import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import apiClient from '../../services/api';
import './ContractPage.css';

function ContractPage({ onComplete, onNext }) {
  const { t } = useTranslation();
  const [contract, setContract] = useState(null);
  const [signature, setSignature] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContract();
  }, []);

  const fetchContract = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/v1/onboarding/contract');
      setContract(response.data);
    } catch (err) {
      console.error('Error fetching contract:', err);
      setError(err.response?.data?.detail || t('onboarding.errors.load_contract_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!signature.trim() || signature.trim().length < 2) {
      setError(t('onboarding.contract.error_signature'));
      return;
    }

    if (!accepted) {
      setError(t('onboarding.contract.error_accept'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await apiClient.post('/api/v1/onboarding/contract', {
        signature: signature.trim(),
        contract_version: contract.version,
      });

      // Complete onboarding
      await apiClient.post('/api/v1/onboarding/complete');

      // Refresh wizard status; it will move to the correct step (success) based on backend status.
      // Avoid double-transition (onComplete + onNext) which can cause the success screen to flash twice.
      await onComplete?.();
    } catch (err) {
      console.error('Error accepting contract:', err);
      setError(err.response?.data?.detail || t('onboarding.errors.accept_contract_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="contract-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('onboarding.contract.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !contract) {
    return (
      <div className="contract-page">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchContract} className="retry-button">
            {t('onboarding.common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contract-page">
      <div className="contract-header">
        <h2>{t('onboarding.contract.title')}</h2>
        <p className="contract-intro">{t('onboarding.contract.intro')}</p>
        <div className="contract-meta">
          <span className="contract-company">
            {contract?.company_name} {contract?.includes_successors && t('onboarding.contract.successors_clause')}
          </span>
          {contract?.version && (
            <span className="contract-version">
              {t('onboarding.contract.version')}: {contract.version}
            </span>
          )}
        </div>
      </div>

      <div className="contract-content">
        {contract?.content ? (
          <div className="contract-text">
            <ReactMarkdown>{contract.content}</ReactMarkdown>
          </div>
        ) : (
          <div className="contract-text">
            <p>{contract?.content || t('onboarding.contract.content_unavailable')}</p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="contract-form">
        <div className="form-group">
          <label htmlFor="signature">{t('onboarding.contract.signature_label')}</label>
          <input
            id="signature"
            type="text"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder={t('onboarding.contract.signature_placeholder')}
            required
            disabled={submitting}
          />
          <small className="form-hint">{t('onboarding.contract.signature_hint')}</small>
        </div>

        <label className="accept-checkbox">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            disabled={submitting}
          />
          <span>{t('onboarding.contract.accept_checkbox')}</span>
        </label>

        {error && <div className="error-message">{error}</div>}

        <button 
          type="submit" 
          className="sign-button"
          disabled={submitting || !signature.trim() || !accepted}
        >
          {submitting ? t('onboarding.contract.signing') : t('onboarding.contract.sign_button')}
        </button>
      </form>
    </div>
  );
}

export default ContractPage;

